import { ref } from 'vue';
import { defineStore } from 'pinia';
import {
  getCurrentProfile,
  updateCurrentProfile,
  updateProfileVisibility,
  updateProfileTimezone,
  updateCurrentProfilePreferences,
} from '@/js/core/utils/profile-api';
import { useTimezoneStore } from '@/js/core/stores/timezone-store';

function errorCodeFromStatus(status) {
  if (status === 401 || status === 403) return 'forbidden';
  if (status === 404) return 'not_found';

  return 'error';
}

function normalizeErrorCode(err) {
  const statusCode = errorCodeFromStatus(err.status);

  if (statusCode !== 'error') {
    return statusCode;
  }

  if (err.code === 'forbidden' || err.code === 'not_found') {
    return err.code;
  }

  return 'error';
}

function safeErrorMessage(code) {
  if (code === 'forbidden') {
    return 'You need to be signed in to view your profile.';
  }

  if (code === 'not_found' || code === 'empty') {
    return 'We could not load your profile. Please try again later.';
  }

  return 'We could not load your profile. Please try again later.';
}

function hydratedCurrentProfile() {
  if (typeof window === 'undefined') {
    return null;
  }

  const profile =
    window.gsdNowAppData?.currentProfile ?? window.gsdNowAppData?.userData?.currentProfile;

  return profile && typeof profile === 'object' ? profile : null;
}

export const useProfileStore = defineStore('profile', () => {
  const profile = ref(null);
  const isLoading = ref(false);
  const error = ref('');
  const code = ref('');
  const status = ref(null);
  const isSaving = ref(false);
  const saveError = ref('');
  const validationErrors = ref({});
  let fetchId = 0;

  function setProfile(nextProfile) {
    profile.value = nextProfile || null;

    if (profile.value) {
      useTimezoneStore().hydrateFromProfile(profile.value);
    }
  }

  setProfile(hydratedCurrentProfile());

  async function fetchCurrentProfile({ force = false } = {}) {
    if (!force && profile.value) {
      return profile.value;
    }

    isLoading.value = true;
    error.value = '';
    code.value = '';
    status.value = null;
    const currentFetchId = ++fetchId;

    try {
      const response = await getCurrentProfile();

      if (currentFetchId !== fetchId) return null;

      setProfile(response?.data || null);

      if (!profile.value) {
        code.value = 'empty';
        error.value = safeErrorMessage(code.value);
      }

      return profile.value;
    } catch (err) {
      if (currentFetchId !== fetchId) return null;

      setProfile(null);
      status.value = err.status || null;
      code.value = normalizeErrorCode(err);
      error.value = safeErrorMessage(code.value);

      return null;
    } finally {
      if (currentFetchId === fetchId) {
        isLoading.value = false;
      }
    }
  }

  async function saveCurrentProfile(data) {
    isSaving.value = true;
    saveError.value = '';
    validationErrors.value = {};

    try {
      const response = await updateCurrentProfile(data);
      setProfile(response?.data || profile.value);

      return profile.value;
    } catch (err) {
      validationErrors.value = err.errors || {};
      saveError.value = err.message || 'Failed to save profile changes. Please try again.';

      throw err;
    } finally {
      isSaving.value = false;
    }
  }

  async function savePreferences(data) {
    isSaving.value = true;
    saveError.value = '';
    validationErrors.value = {};

    const mergedPreferences = {
      ...(profile.value?.preferences || {}),
      ...data,
    };

    setProfile({ ...profile.value, preferences: mergedPreferences });

    try {
      const response = await updateCurrentProfilePreferences(mergedPreferences);
      setProfile(response?.data || profile.value);

      return profile.value;
    } catch (err) {
      validationErrors.value = err.errors || {};
      saveError.value = err.message || 'Failed to save profile preferences. Please try again.';

      throw err;
    } finally {
      isSaving.value = false;
    }
  }

  async function saveProfileVisibility(profileVisibility) {
    isSaving.value = true;
    saveError.value = '';
    validationErrors.value = {};

    try {
      const response = await updateProfileVisibility(profileVisibility);
      setProfile(response?.data || profile.value);

      return profile.value;
    } catch (err) {
      validationErrors.value = err.errors || {};
      saveError.value = err.message || 'Failed to save profile visibility. Please try again.';

      throw err;
    } finally {
      isSaving.value = false;
    }
  }

  async function saveTimezoneSettings(data) {
    isSaving.value = true;
    saveError.value = '';
    validationErrors.value = {};

    try {
      const response = await updateProfileTimezone(data);
      setProfile(response?.data || profile.value);

      return profile.value;
    } catch (err) {
      validationErrors.value = err.errors || {};
      saveError.value = err.message || 'Failed to save timezone settings. Please try again.';

      throw err;
    } finally {
      isSaving.value = false;
    }
  }

  function reset() {
    setProfile(null);
    useTimezoneStore().reset();
    isLoading.value = false;
    error.value = '';
    code.value = '';
    status.value = null;
    isSaving.value = false;
    saveError.value = '';
    validationErrors.value = {};
  }

  return {
    profile,
    isLoading,
    error,
    code,
    status,
    isSaving,
    saveError,
    validationErrors,
    fetchCurrentProfile,
    saveCurrentProfile,
    savePreferences,
    saveProfileVisibility,
    saveTimezoneSettings,
    reset,
  };
});
